import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import { handleApiError, handleGenericError } from "../../utils/errorHandler";
import {
  Card,
  Text,
  Group,
  Stack,
  Loader,
  Alert,
  Title,
  Button,
  Badge,
  Modal,
  TextInput,
  Switch,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import {
  IconAlertCircle,
  IconRefresh,
  IconEdit,
  IconTrash,
  IconCalendar,
} from "@tabler/icons-react";
import PageContent from "../shared/PageContent/PageContent";

function SeasonDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getVerifiedJWT } = useAuth();
  const { showSuccess, showError } = useToast();
  const [season, setSeason] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editModalOpened, setEditModalOpened] = useState(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    start_date: null,
    end_date: null,
    lock_in_date: null,
    is_active: false,
  });

  const fetchSeason = async () => {
    try {
      setLoading(true);
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        setError("Failed to fetch season");
        return;
      }

      const response = await fetch(getApiEndpoint(`/v1/seasons/${id}`), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setSeason(result.data ?? result);
        setError(null);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.message || "Failed to fetch season");
      }
    } catch (err) {
      handleGenericError(err, "Failed to fetch season");
      setError("Failed to fetch season");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSeason();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const formatDate = dateString => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "Invalid Date";
    }
  };

  const handleOpenEdit = () => {
    if (!season) return;
    setFormData({
      name: season.name || "",
      start_date: season.start_date ? new Date(season.start_date) : null,
      end_date: season.end_date ? new Date(season.end_date) : null,
      lock_in_date: season.lock_in_date ? new Date(season.lock_in_date) : null,
      is_active: season.is_active || false,
    });
    setEditModalOpened(true);
  };

  const handleSubmitEdit = async () => {
    if (
      !formData.name ||
      !formData.start_date ||
      !formData.end_date ||
      !formData.lock_in_date
    ) {
      showError("Please fill in all required fields");
      return;
    }

    try {
      setSubmitting(true);
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        throw new Error("Authentication failed");
      }

      const toISO = date => (date ? new Date(date).toISOString() : null);

      const payload = {
        name: formData.name,
        start_date: toISO(formData.start_date),
        end_date: toISO(formData.end_date),
        lock_in_date: toISO(formData.lock_in_date),
        is_active: formData.is_active,
      };

      const response = await fetch(getApiEndpoint(`/v1/seasons/${id}`), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        showSuccess("Season updated successfully!");
        setEditModalOpened(false);
        fetchSeason();
      } else {
        const errorData = await response.json().catch(() => ({}));
        handleApiError(
          new Error(errorData.message || `HTTP ${response.status}`),
          errorData.message || "Failed to update season"
        );
      }
    } catch (err) {
      handleGenericError(err, "Failed to update season");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      setSubmitting(true);
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        throw new Error("Authentication failed");
      }

      const response = await fetch(getApiEndpoint(`/v1/seasons/${id}`), {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        showSuccess("Season deleted successfully!");
        setDeleteModalOpened(false);
        navigate("/selling-years");
      } else {
        const errorData = await response.json().catch(() => ({}));
        handleApiError(
          new Error(errorData.message || `HTTP ${response.status}`),
          errorData.message || "Failed to delete season"
        );
      }
    } catch (err) {
      handleGenericError(err, "Failed to delete season");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageContent
      title={season?.name || "Season"}
      breadcrumbs={[
        { label: "Selling Years", path: "/selling-years" },
        { label: season?.name || "Season" },
      ]}
    >
      <Stack gap="lg">
        {loading && (
          <Group justify="center" py="xl">
            <Loader size="md" />
            <Text>Loading season...</Text>
          </Group>
        )}

        {error && !loading && (
          <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
            <Group justify="space-between">
              <Text>{error}</Text>
              <Button
                variant="outline"
                size="sm"
                leftSection={<IconRefresh size={16} />}
                onClick={fetchSeason}
              >
                Retry
              </Button>
            </Group>
          </Alert>
        )}

        {!loading && !error && season && (
          <Card padding="lg" radius="md" withBorder>
            <Stack gap="md">
              <Group justify="space-between" align="center">
                <Group gap="sm" align="center">
                  <IconCalendar size={20} />
                  <Title order={3}>{season.name}</Title>
                  <Badge
                    color={season.is_active ? "green" : "gray"}
                    variant="light"
                  >
                    {season.is_active ? "Active" : "Inactive"}
                  </Badge>
                </Group>
                <Group gap="xs">
                  <Button
                    variant="outline"
                    leftSection={<IconEdit size={16} />}
                    onClick={handleOpenEdit}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    color="red"
                    leftSection={<IconTrash size={16} />}
                    onClick={() => setDeleteModalOpened(true)}
                  >
                    Delete
                  </Button>
                </Group>
              </Group>

              <Group gap="xs">
                <Text size="sm" c="dimmed" w={100}>
                  Start:
                </Text>
                <Text size="sm">{formatDate(season.start_date)}</Text>
              </Group>
              <Group gap="xs">
                <Text size="sm" c="dimmed" w={100}>
                  End:
                </Text>
                <Text size="sm">{formatDate(season.end_date)}</Text>
              </Group>
              <Group gap="xs">
                <Text size="sm" c="dimmed" w={100}>
                  Lock-in:
                </Text>
                <Text size="sm">{formatDate(season.lock_in_date)}</Text>
              </Group>
              <Group gap="xs">
                <Text size="sm" c="dimmed" w={100}>
                  Reps:
                </Text>
                <Text size="sm">{season.rep_count ?? 0}</Text>
              </Group>
            </Stack>
          </Card>
        )}
      </Stack>

      <Modal
        opened={editModalOpened}
        onClose={() => setEditModalOpened(false)}
        title="Edit Season"
        size="md"
      >
        <Stack gap="md">
          <TextInput
            label="Season Name"
            placeholder="e.g., Spring 2025"
            value={formData.name}
            onChange={e => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <DateInput
            label="Start Date"
            placeholder="Select start date"
            value={formData.start_date}
            onChange={date => setFormData({ ...formData, start_date: date })}
            required
          />
          <DateInput
            label="End Date"
            placeholder="Select end date"
            value={formData.end_date}
            onChange={date => setFormData({ ...formData, end_date: date })}
            required
          />
          <DateInput
            label="Lock-in Date"
            placeholder="Select lock-in date"
            value={formData.lock_in_date}
            onChange={date => setFormData({ ...formData, lock_in_date: date })}
            required
          />
          <Switch
            label="Active Season"
            checked={formData.is_active}
            onChange={e =>
              setFormData({
                ...formData,
                is_active: e.currentTarget.checked,
              })
            }
          />
          <Group justify="flex-end" mt="md">
            <Button
              variant="subtle"
              onClick={() => setEditModalOpened(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmitEdit} loading={submitting}>
              Update
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={deleteModalOpened}
        onClose={() => setDeleteModalOpened(false)}
        title="Delete Season"
        size="sm"
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to delete the season "{season?.name}"? This
            action cannot be undone.
          </Text>
          <Group justify="flex-end" mt="md">
            <Button
              variant="subtle"
              onClick={() => setDeleteModalOpened(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button color="red" onClick={handleDelete} loading={submitting}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </PageContent>
  );
}

export default SeasonDetailsPage;
